import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TextField,
  IconButton,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

const format = (val) =>
  `₱${(val || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

const percentDiff = (prev, curr) => {
  if (!prev) return "—";
  const diff = ((curr - prev) / prev) * 100;
  const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
  const color = diff > 0 ? "green" : diff < 0 ? "red" : "gray";
  return (
    <span style={{ color }}>
      {arrow} {Math.abs(diff).toFixed(2)}%
    </span>
  );
};

const SalesPopularMenuItem = ({ data, start, end, prevStart, prevEnd }) => {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const mostPopularItems = useMemo(() => {
    if (!data?.mostPopularItems) return [];
    return data.mostPopularItems;
  }, [data]);

  const filteredItems = useMemo(() => {
    return mostPopularItems.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [mostPopularItems, search]);

  const sortedItems = useMemo(() => {
    if (!sortField) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      const aVal =
        sortField === "qty" ? a.qty?.current || 0 : a.amount?.current || 0;
      const bVal =
        sortField === "qty" ? b.qty?.current || 0 : b.amount?.current || 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filteredItems, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const displayedItems = showAll ? sortedItems : sortedItems.slice(0, 10);
  if (!mostPopularItems.length) return null;

  const sortIcon = (field) =>
    sortField === field ? (
      sortDirection === "asc" ? (
        <ArrowUpwardIcon fontSize="inherit" />
      ) : (
        <ArrowDownwardIcon fontSize="inherit" />
      )
    ) : null;

  return (
    <Paper sx={{ mt: 4, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
        }}
      >
        <Typography variant="h6" gutterBottom align="center">
          Most Popular Menu Items
        </Typography>
        <Box sx={{ position: "absolute", right: 0 }}>
          <IconButton onClick={() => setShowAll(!showAll)} size="small">
            {showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Typography
        variant="subtitle2"
        align="center"
        color="text.secondary"
        gutterBottom
      >
        {prevStart.format("MM/DD")}–{prevEnd.format("MM/DD")} vs{" "}
        {start.format("MM/DD")}–{end.format("MM/DD")}
      </Typography>

      {showAll && (
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Prev Qty</TableCell>
            <TableCell align="center"></TableCell>
            <TableCell
              align="right"
              sx={{ cursor: "pointer" }}
              onClick={() => toggleSort("qty")}
            >
              Curr Qty {sortIcon("qty")}
            </TableCell>
            <TableCell align="right">Prev Amt</TableCell>
            <TableCell align="center"></TableCell>
            <TableCell
              align="right"
              sx={{ cursor: "pointer" }}
              onClick={() => toggleSort("amount")}
            >
              Curr Amt {sortIcon("amount")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedItems.map((item) => (
            <TableRow key={item.name}>
              <TableCell>{item.name}</TableCell>
              <TableCell align="right">{item.qty?.previous || 0}</TableCell>
              <TableCell align="center">
                {percentDiff(item.qty?.previous, item.qty?.current)}
              </TableCell>
              <TableCell align="right">{item.qty?.current || 0}</TableCell>
              <TableCell align="right">
                {format(item.amount?.previous)}
              </TableCell>
              <TableCell align="center">
                {percentDiff(item.amount?.previous, item.amount?.current)}
              </TableCell>
              <TableCell align="right">
                {format(item.amount?.current)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default SalesPopularMenuItem;
